import React, { useEffect, useMemo, useState } from "react";
import InstructionRow, { EmptyInstructionRow, InstructionRowHeaders } from "./InstructionRow";
import CashSourceRow from "./CashSourceRow";
import { ThemedSpan } from "../../../components/utilities";
import { NumericFormat } from "react-number-format";
import InstructionLabelRow from "./InstructionLabelRow";
import { useHeadOfficeApplicationContext } from "../../../hooks/HeadOfficeApplicationContext";
import EarmarkCashInstructionRow from "./EarmarkCashInstructionRow";

const IsaSubscriptionCash = ({ data, updateFn, cashSourceUpdateFn, showCashUpdatedModal, showRecalculateTransactionsModal, setTotalCashRaised, deductAdviceFeeFromRemainingBalance }) => {

    const { application: { hasBuyTransactions } } = useHeadOfficeApplicationContext();

    const totalCashSubscription = useMemo(() => {
        return data.cashSources.reduce((total, curr) => total + curr.cashAmount, 0) + data.cashFromClientAmount + data.cashFromApsAmount;
    }, [data]);

    const remainingBalance = useMemo(() => {
        var balance = totalCashSubscription - (
            data.clientWithdrawalAmount
            + (data.earmarkCashAmount ?? data.totalIncomeAmount)
            + (deductAdviceFeeFromRemainingBalance ? data.deductAdviceFeeAmount : 0)
            + data.investAmount
        );

        return balance;
    }, [data]);

    const [initialTotalCashSubscription, setInitialTotalCashSubscription] = useState(totalCashSubscription);

    const [isInvestmentInitiallyEqualToCashRaised, setIsInvestmentInitiallyEqualToCashRaised] = useState(totalCashSubscription === data.investAmount);

    useEffect(() => {
        setTotalCashRaised(totalCashSubscription);
    }, [totalCashSubscription, setTotalCashRaised]);

    useEffect(() => {
        if (totalCashSubscription === initialTotalCashSubscription) return;
        // these will (rightfully) not update in time for the showCashUpdatedModal() call, but the change will reflect for the next run
        // this is the desired behaviour here as we want the values before the update to be passed in
        setInitialTotalCashSubscription(totalCashSubscription);
        setIsInvestmentInitiallyEqualToCashRaised(totalCashSubscription === data.investAmount);
        showCashUpdatedModal(isInvestmentInitiallyEqualToCashRaised);
    }, [totalCashSubscription]);


    return <React.Fragment>
        <div className="container-fluid mb-4">
            <CashSourceRow
                data={data}
                updateFn={updateFn}
                label={"Cash from Client"}
                appliesProperty={"cashFromClientApplies"}
                cashAmountProperty={"cashFromClientAmount"}
                receivedDateProperty={"cashFromClientReceivedDate"}
                receivedConfirmedByNameProperty={"cashFromClientReceivedConfirmedByName"}
                commentProperty={"cashFromClientComment"}
                receivedConfirmedSetDateProperty={"cashFromClientReceivedConfirmedSetDate"}
            />

            <CashSourceRow
                data={data}
                updateFn={updateFn}
                label={"Cash from APS"}
                appliesProperty={"cashFromApsApplies"}
                cashAmountProperty={"cashFromApsAmount"}
                receivedDateProperty={"cashFromApsReceivedDate"}
                receivedConfirmedByNameProperty={"cashFromApsReceivedConfirmedByName"}
                commentProperty={"cashFromApsComment"}
                receivedConfirmedSetDateProperty={"cashFromApsReceivedConfirmedSetDate"}
            />

            {
                data.cashSources && data.cashSources.map((source) => <CashSourceRow
                    key={source.sourceId}
                    data={source}
                    updateFn={(property, newValue, appliesProperty) => cashSourceUpdateFn(source.sourceId, property, newValue, appliesProperty)}
                    label={`Cash from ${source.providerReference}`}
                    appliesProperty={"cashApplies"}
                    cashAmountProperty={"cashAmount"}
                    receivedDateProperty={"cashReceivedDate"}
                    receivedConfirmedByNameProperty={"cashReceivedConfirmedByName"}
                    commentProperty={"comment"}
                    receivedConfirmedSetDateProperty={"cashReceivedConfirmedSetDate"}
                />)
            }
            <div className="row mt-3">
                <div className="col-2">
                    <b><ThemedSpan>Total Cash Subscription</ThemedSpan></b>
                </div>
                <div className="col-2 text-end">
                    <b><NumericFormat value={totalCashSubscription} prefix={"£"} thousandSeparator displayType="text" decimalScale={2} fixedDecimalScale /></b>
                </div>
            </div>
        </div>
        <div className="container-fluid">
            <InstructionRowHeaders />
            <InstructionRow
                data={data}
                updateFn={updateFn}
                label={"Instruct Income"}
                appliesProperty={"incomeApplies"}
                frequencyProperty={"incomeFrequency"}
                cashAmountProperty={"incomeAmount"}
                dateInstructedProperty={"incomeInstructedDate"}
                instructedDateSetProperty={"incomeInstructedSetDate"}
                instructedByProperty={"incomeInstructedByName"}
                dateConfirmedProperty={"incomeConfirmedDate"}
                confirmedDateSetProperty={"incomeConfirmedSetDate"}
                confirmedByProperty={"incomeConfirmedByName"}
            />

            <InstructionRow
                data={data}
                updateFn={updateFn}
                label={"Natural Income"}
                appliesProperty={"naturalIncome"}
                dateInstructedProperty={"naturalIncomeInstructedDate"}
                instructedDateSetProperty={"naturalIncomeInstructedSetDate"}
                instructedByProperty={"naturalIncomeInstructedByName"}
                dateConfirmedProperty={"naturalIncomeConfirmedDate"}
                confirmedDateSetProperty={"naturalIncomeConfirmedSetDate"}
                confirmedByProperty={"naturalIncomeConfirmedByName"}
            />

            {/* Spacer row to separate income not relevant to calculation */}
            <EmptyInstructionRow />

            <InstructionRow
                data={data}
                updateFn={updateFn}
                label={"Client Withdrawal"}
                appliesProperty={"clientWithdrawalApplies"}
                cashAmountProperty={"clientWithdrawalAmount"}
                dateInstructedProperty={"clientWithdrawalInstructedDate"}
                instructedDateSetProperty={"clientWithdrawalInstructedSetDate"}
                instructedByProperty={"clientWithdrawalInstructedByName"}
                dateConfirmedProperty={"clientWithdrawalConfirmedDate"}
                confirmedDateSetProperty={"clientWithdrawalConfirmedSetDate"}
                confirmedByProperty={"clientWithdrawalConfirmedByName"}
            />

            <EarmarkCashInstructionRow
                data={data}
                updateFn={updateFn}
            />

            <InstructionRow
                data={data}
                updateFn={updateFn}
                label={"Deduct Advice Fee"}
                appliesProperty={"deductAdviceFeeApplies"}
                cashAmountProperty={"deductAdviceFeeAmount"}
                dateInstructedProperty={"deductAdviceFeeAmountInstructedDate"}
                instructedDateSetProperty={"deductAdviceFeeAmountInstructedSetDate"}
                instructedByProperty={"deductAdviceFeeAmountInstructedByName"}
                dateConfirmedProperty={"deductAdviceFeeConfirmedDate"}
                confirmedDateSetProperty={"deductAdviceFeeConfirmedSetDate"}
                confirmedByProperty={"deductAdviceFeeConfirmedByName"}
            />

            <InstructionRow
                data={data}
                updateFn={updateFn}
                label={hasBuyTransactions ? "Net Amount to Invest" : "Retain In Cash"}
                appliesProperty={"investAmountApplies"}
                cashAmountProperty={"investAmount"}
                dateInstructedProperty={"investAmountInstructedDate"}
                instructedDateSetProperty={"investAmountInstructedSetDate"}
                instructedByProperty={"investAmountInstructedByName"}
                dateConfirmedProperty={"investAmountConfirmedDate"}
                confirmedDateSetProperty={"investAmountConfirmedSetDate"}
                confirmedByProperty={"investAmountConfirmedByName"}
                recalculateTransactionsButton
                onRecalculateTransactionsClick={() => showRecalculateTransactionsModal(true)}
            />

            <InstructionLabelRow
                className={"mt-4 mb-2"}
                label={<ThemedSpan variant={remainingBalance !== 0 ? "danger" : null}>Remaining Balance</ThemedSpan>}
                value={<NumericFormat
                    className={remainingBalance !== 0 ? "text-danger" : null}
                    value={remainingBalance}
                    prefix={"£"}
                    thousandSeparator
                    displayType="text"
                    decimalScale={2}
                    fixedDecimalScale
                />}
            />
        </div>
    </React.Fragment>
};

export default IsaSubscriptionCash;